import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Eye, Search, X } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * CustomerViewToggle — "View as Customer" floating pill (admin-only).
 *
 * 2026-06-05 rewrite: clicking this pill now opens a customer picker
 * and (on select) mints a customer-shaped JWT server-side, fully
 * swapping the active session into that customer's identity.
 *
 * Why the rewrite: previously this was just a UI mask (sessionStorage
 * flag) — admin chrome would hide, but the cart, prices, addresses
 * still belonged to the admin's own account. Operators reported they
 * couldn't see the customer's live cart from here. Now the pill does
 * the same thing the (now-removed) "Impersonate" pill used to do:
 * full JWT swap → admin literally is that customer until they exit.
 *
 * Exit is handled by the persistent ImpersonationBanner (red bar at
 * top with one-click restore).
 */
const CustomerViewToggle = () => {
  const { token, isAuthenticated, startImpersonation, isImpersonating } = useAuth();
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState(false);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debRef = useRef(null);

  // Same home-route gating as before — keeps the pill on landing surfaces only.
  const HOME_ROUTES = ['/', '/select-category'];
  const isHomeRoute = HOME_ROUTES.includes(location.pathname)
    || location.pathname.startsWith('/search/');

  useEffect(() => {
    if (!isAuthenticated || !token) {
      setIsAdmin(false);
      return;
    }
    let cancel = false;
    axios.get(`${API}/admin/check`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!cancel) setIsAdmin(!!r.data?.is_admin); })
      .catch(() => { if (!cancel) setIsAdmin(false); });
    return () => { cancel = true; };
  }, [token, isAuthenticated]);

  const fetchCustomers = useCallback(async (q) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/customers/lite`, {
        params: { q, limit: 50 },
        headers: { Authorization: `Bearer ${token}` },
      });
      setResults(r.data?.customers || []);
    } catch (e) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!open) return;
    clearTimeout(debRef.current);
    debRef.current = setTimeout(() => fetchCustomers(query), 220);
    return () => clearTimeout(debRef.current);
  }, [open, query, fetchCustomers]);

  // Gating:
  // - non-admin → never show
  // - already impersonating → banner handles exit, pill stays hidden
  // - non-home route → hidden (UX request, was the same before)
  if (!isAdmin) return null;
  if (isImpersonating?.()) return null;
  if (!isHomeRoute) return null;

  const handleSelect = async (c) => {
    setOpen(false);
    await startImpersonation(c.customer_id);
  };

  return (
    <div
      className="fixed left-2 md:left-6 z-[60]"
      style={{ top: 'calc(4rem + 8px)' }}
      data-testid="customer-view-toggle-wrap"
    >
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-full border bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400 shadow-sm transition-all text-[11px] md:text-xs font-semibold px-2.5 md:px-3 py-1 md:py-1.5"
        title="Pick a customer and see exactly what they see (live cart, prices, orders)"
        data-testid="enter-customer-view-btn"
      >
        <Eye className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">View as Customer</span>
        <span className="inline sm:hidden">Customer</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] bg-black/40 flex items-start justify-center p-4 md:pt-24"
          onClick={() => setOpen(false)}
          data-testid="customer-view-picker-backdrop"
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col"
            onClick={e => e.stopPropagation()}
            data-testid="customer-view-picker"
          >
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <div className="font-semibold text-gray-800 text-sm">Pick a customer to view as</div>
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-700" data-testid="customer-view-picker-close"><X className="w-4 h-4" /></button>
            </div>
            <div className="px-4 py-2 border-b">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2 top-2.5 text-gray-400" />
                <input
                  autoFocus
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder="Search by name, mobile, company, customer id..."
                  className="w-full pl-8 pr-2 py-2 text-sm border rounded outline-none focus:border-amber-400"
                  data-testid="customer-view-picker-search"
                />
              </div>
            </div>
            <div className="overflow-y-auto flex-1">
              {loading && <div className="px-4 py-6 text-center text-xs text-gray-500">Searching...</div>}
              {!loading && results.length === 0 && (
                <div className="px-4 py-6 text-center text-xs text-gray-500">No customers found</div>
              )}
              {!loading && results.map((c) => (
                <button
                  key={c.customer_id}
                  onClick={() => handleSelect(c)}
                  className="w-full text-left px-4 py-2.5 hover:bg-amber-50 border-b last:border-b-0 transition-colors"
                  data-testid={`customer-view-pick-${c.customer_id}`}
                >
                  <div className="text-sm font-medium text-gray-900 truncate">{c.name || '(no name)'}</div>
                  <div className="text-[11px] text-gray-500 truncate">
                    {c.customer_id} · {c.mobile || '—'}
                    {c.company_name ? ` · ${c.company_name}` : ''}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerViewToggle;
